import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Heart, Shield, Megaphone, GraduationCap, Send, CheckCircle, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Newsletter from '../components/Newsletter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const membershipTypes = [
  {
    id: 'general',
    name: 'General Member',
    icon: Users,
    description: 'Join our community and stay informed about civil and human rights issues.',
    benefits: ['Newsletter updates', 'Event invitations', 'Community resources']
  },
  {
    id: 'volunteer',
    name: 'Volunteer',
    icon: Heart,
    description: 'Dedicate your time and skills to support our mission.',
    benefits: ['All General benefits', 'Volunteer opportunities', 'Training sessions']
  },
  {
    id: 'advocate',
    name: 'Advocate',
    icon: Megaphone,
    description: 'Actively participate in campaigns and advocacy efforts.',
    benefits: ['All Volunteer benefits', 'Campaign participation', 'Leadership opportunities']
  },
  {
    id: 'youth',
    name: 'Youth Member',
    icon: GraduationCap,
    description: 'For young people ages 14-24 committed to justice.',
    benefits: ['Youth programs', 'Mentorship', 'Educational workshops']
  }
];

const interestOptions = [
  'Civil Rights Advocacy',
  'Immigration Justice',
  'Community Organizing',
  'Cultural Education',
  'Youth Programs',
  'Legal Support',
  'Media & Communications',
  'Event Planning',
  'Fundraising',
  'Translation Services'
];

const howHeardOptions = [
  'Social Media',
  'Friend or Family',
  'Community Event',
  'News Article',
  'XRU Website',
  'Partner Organization',
  'School/University',
  'Other'
];

const JoinXRU = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: 'Minneapolis',
    state: 'MN',
    zip_code: '',
    membership_type: 'general',
    interests: [],
    skills: '',
    how_heard: '',
    message: ''
  });

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/members`, formData);
      setIsSubmitted(true);
      toast.success('Welcome to XRU! Your membership has been registered.');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already registered as a member.');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-stone-50 pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-teal-600" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-stone-800 mb-4">Welcome to XRU!</h1>
          <p className="text-lg text-stone-600 mb-4">
            Thank you for joining the Xicano Rights Union. Together, we are stronger.
          </p>
          <p className="text-stone-500 mb-8">
            You'll receive a welcome email shortly with more information about how to get involved.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
            >
              Return Home
            </Link>
            <Link
              to="/programs"
              className="px-8 py-3 border border-stone-300 text-stone-700 font-semibold rounded-lg hover:bg-stone-100 transition-colors"
            >
              Explore Our Work
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
            <Star size={16} />
            <span>Become Part of the Movement</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Join the <span className="text-amber-300">Xicano Rights Union</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Stand with us in the fight for civil and human rights, cultural preservation, and justice.
            Your voice and participation make our community stronger.
          </p>
        </div>
      </section>

      {/* Membership Types */}
      <section className="py-12 -mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {membershipTypes.map(type => {
              const Icon = type.icon;
              const isSelected = formData.membership_type === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, membership_type: type.id })}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-teal-500 bg-white shadow-lg shadow-teal-500/20'
                      : 'border-stone-200 bg-white hover:border-teal-300'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                    isSelected ? 'bg-teal-100' : 'bg-stone-100'
                  }`}>
                    <Icon className={isSelected ? 'text-teal-600' : 'text-stone-500'} size={24} />
                  </div>
                  <h3 className="font-bold text-stone-800 mb-1">{type.name}</h3>
                  <p className="text-sm text-stone-500 mb-3">{type.description}</p>
                  <ul className="space-y-1">
                    {type.benefits.map((benefit, i) => (
                      <li key={i} className="text-xs text-stone-600 flex items-center gap-1">
                        <CheckCircle size={12} className="text-teal-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Users size={20} className="text-teal-600" />
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="(xxx) xxx-xxxx"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4">Location</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-2">Street Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">ZIP</label>
                    <input
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                      placeholder="55xxx"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Interests */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Heart size={20} className="text-red-500" />
                Areas of Interest
              </h2>
              <p className="text-sm text-stone-500 mb-4">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {interestOptions.map(interest => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      formData.interests.includes(interest)
                        ? 'bg-teal-600 text-white'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Skills & Additional Info */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4">Additional Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Skills You Can Contribute</label>
                  <input
                    type="text"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="e.g., Legal expertise, Spanish translation, graphic design..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">How did you hear about XRU?</label>
                  <select
                    value={formData.how_heard}
                    onChange={(e) => setFormData({ ...formData, how_heard: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  >
                    <option value="">Select...</option>
                    {howHeardOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Message (Optional)</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500 resize-none"
                    placeholder="Tell us why you want to join or anything else you'd like us to know..."
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-teal-600 hover:bg-teal-700 disabled:bg-stone-300 text-white font-semibold text-lg rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <>
                  <Send size={20} />
                  Join XRU
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-16 bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Why Join <span className="text-amber-400">XRU</span>?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Protect Our Community</h3>
              <p className="text-stone-300">Stand together against civil and human rights violations and support those affected by unjust policies.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Megaphone className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Amplify Our Voice</h3>
              <p className="text-stone-300">Join campaigns, attend rallies, and advocate for policies that protect our rights.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Build Solidarity</h3>
              <p className="text-stone-300">Connect with like-minded individuals and organizations fighting for justice.</p>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default JoinXRU;
